import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { normalizeFeatureName, normalizeAction } from "@/lib/feature-mapping";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";

export interface ResourcePermission {
  resource_key: string;
  resource_name: string;
  description?: string;
  module_key?: string;
}

export interface UserPermission {
  resource_key: string;
  view: boolean;
  create: boolean;
  edit: boolean;  // Mantido para compatibilidade (mapeado para update internamente)
  delete: boolean;
}

export interface UserPermissionsMap {
  [resource_key: string]: UserPermission;
}

export interface FeaturePermissions {
  view: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface AllPermissionsResponse {
  [feature: string]: FeaturePermissions;
}

type FeatureRow = Database["public"]["Tables"]["features"]["Row"] & {
  modules: { value: string | null } | null;
};

/**
 * Hook to check if current user has a specific permission
 * Permissions are checked in the context of the current clinic
 *
 * IMPORTANTE: Após migração 20251127000000, features foram consolidadas.
 * Use os nomes consolidados: 'appointments', 'patients', etc. (sem sufixo _view/_create)
 *
 * @example
 * const hasPermission = useHasPermission('patients', 'view');
 */
export function useHasPermission(
  resource: string,
  action: "view" | "create" | "edit" | "delete" = "view"
) {
  const normalizedFeature = normalizeFeatureName(resource);
  const normalizedAction = normalizeAction(action);
  const { data: permissionsMap, isLoading } = usePermissionsMap();

  const hasPermission =
    permissionsMap?.[normalizedFeature]?.[normalizedAction] ??
    (normalizedFeature === "applications"
      ? permissionsMap?.vaccines?.[normalizedAction] ?? false
      : false);

  return {
    data: hasPermission,
    isLoading,
  };
}

/**
 * Hook to get all permissions for current user in the current clinic context
 *
 * IMPORTANTE: Após migração 20251127000000, retorna features consolidadas
 * no formato: { appointments: { view, create, update, delete }, ... }
 */
export function useUserPermissions() {
  const permissionsQuery = usePermissionsMap();

  return {
    ...permissionsQuery,
    data: (permissionsQuery.data ?? {}) as AllPermissionsResponse,
  };
}

/**
 * Hook to get all available features
 *
 * IMPORTANTE: Após migração 20251127000000, busca da tabela 'features'
 */
export function useFeatures(filters?: { module_id?: string }) {
  return useQuery<FeatureRow[]>({
    queryKey: ["features", filters],
    queryFn: async () => {
      let query = supabase
        .from("features")
        .select("*, modules(value)")
        .order("value");

      if (filters?.module_id) {
        query = query.eq("module_id", filters.module_id);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching features:", error);
        return [];
      }

      return data ?? [];
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes (features don't change often)
  });
}

/**
 * @deprecated Use useFeatures instead
 * Mantido para compatibilidade com código antigo
 */
export function useResources(filters?: { module_key?: string }) {
  const query = useFeatures();
  return {
    ...query,
    data: query.data?.filter(feature => !filters?.module_key || feature.modules?.value === filters.module_key).map((feature): ResourcePermission => ({
      resource_key: feature.value,
      resource_name: feature.value,
      module_key: feature.modules?.value ?? undefined,
    })),
  };
}

/**
 * Helper hook that returns a boolean directly (useful for conditional rendering)
 */
export function useHasPermissionSync(
  resource: string,
  action: "view" | "create" | "edit" | "delete" = "view"
) {
  const { data: hasPermission, isLoading } = useHasPermission(resource, action);
  return { hasPermission: hasPermission ?? false, isLoading };
}
